import { useTheme } from "next-themes";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AdminDashboard } from "./components/admin/AdminDashboard";
import { CountryCurrencySetup } from "./components/admin/CountryCurrencySetup";
import { DataSourceConfiguration } from "./components/admin/DataSourceConfiguration";
import { GeneralSettings } from "./components/admin/GeneralSettings";
import { SystemTerminologySettings } from "./components/admin/SystemTerminologySettings";
import { DynamicDashboard } from "./components/dashboard/DynamicDashboard";
import { Header } from "./components/layout/Header";
import { Sidebar } from "./components/layout/Sidebar";
import { TourMenu } from "./components/tours/TourMenu";
import { TourOverlay } from "./components/tours/TourOverlay";
import { WelcomePrompt } from "./components/tours/WelcomePrompt";
import { SidebarInset, SidebarProvider } from "./components/ui/sidebar";
import { AppProvider, useApp } from "./contexts/AppContext";
import { AppStateProvider } from "./contexts/AppStateContext";
import { AssistantProvider, useAssistant } from "./contexts/AssistantContext";
import { BaseForecastProvider } from "./contexts/BaseForecastContext";
import { DataSourceProvider } from "./contexts/DataSourceContext";
import { DrillDownProvider } from "./contexts/DrillDownContext";
import { JurisdictionProvider } from "./contexts/JurisdictionContext";
import { PreferencesProvider } from "./contexts/PreferencesContext";
import { ScenarioProvider } from "./contexts/ScenarioContext";
import { TourProvider, useTour } from "./contexts/TourContext";
import {
	UserPreferenceDefaultsProvider,
	useUserPreferenceDefaults,
} from "./contexts/UserPreferenceDefaultsContext";
import {
	UserPreferencesProvider,
	useUserPreferences,
} from "./contexts/UserPreferencesContext";
import { useUserProfile } from "./hooks/useUserProfile";
import { WootricProvider } from "./providers/WootricProvider";

// DataSourcePrioritisation is not yet implemented — placeholder component used until built
const DataSourcePrioritisation: React.FC<{
	onNavigate: (page: string) => void;
	highlightSourceType?: string;
}> = () => (
	<div className="p-8 text-center text-gray-500">
		Data Source Prioritisation — coming soon
	</div>
);

import { AdminDashboardLayout } from "./components/admin/AdminDashboardLayout";
import { ApprovalWorkflowsPage } from "./components/admin/ApprovalWorkflowsPage";
import { RolesPermissionsContainer } from "./components/admin/RolesPermissionsContainer";
import { UserManagement } from "./components/admin/UserManagement";
import { UserPreferenceDefaults } from "./components/admin/UserPreferenceDefaults";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Forecast3Year } from "./components/forecast/Forecast3Year";
import { Forecast12Month } from "./components/forecast/Forecast12Month";
import { ForecastRefreshBanner } from "./components/forecast/ForecastRefreshBanner";
import { ForecastRefreshPage } from "./components/forecast/ForecastRefreshPage";
import { ForecastTimeline } from "./components/forecast/ForecastTimeline";
import { HelpDrawer } from "./components/help/HelpDrawer";
import { LandingPageComingSoon } from "./components/LandingPageComingSoon";
import { LoadingScreen } from "./components/LoadingScreen";
import { OnboardingGate } from "./components/onboarding/OnboardingGate";
import { ProtectedRoute } from "./components/permissions/ProtectedRoute";
import { RecurringTransactionsPage } from "./components/recurring-transactions/RecurringTransactionsPage";
import { ReportsPage } from "./components/reports/ReportsPage";
import { NewScenariosPage } from "./components/scenarios/NewScenariosPage";
import { BankAccountsPage } from "./components/settings/BankAccountsPage";
import { CategoryMappingPage } from "./components/settings/CategoryMappingPage";
import { SettingsPage } from "./components/settings-page";
import { TransactionsPage } from "./components/transactions/TransactionsPage";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Toaster } from "./components/ui/toaster";
import { VarianceAnalysisPage } from "./components/variance-analysis-page";
import { ActionsProvider } from "./contexts/ActionsContext";
import { ForecastRefreshProvider } from "./contexts/ForecastRefreshContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import { RecommendationsProvider } from "./contexts/RecommendationsContext";
import { UsersProvider } from "./contexts/UsersContext";
import { usePermissions } from "./hooks/usePermissions";
import { getRoleTemplateId } from "./lib/permissions";
import { ActionsPage } from "./pages/ActionsPage";
import { AIRecommendationsPage } from "./pages/AIRecommendationsPage";
import type { Permission } from "./types/permissions";

// Wrapper component to integrate Wootric with user profile from AppContext
const WootricWrapper: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const { user: wootricUser } = useUserProfile();
	return <WootricProvider user={wootricUser}>{children}</WootricProvider>;
};

// ThemeSync: always-mounted component that applies the current user's saved
// theme whenever the active user changes. Read-only — never writes to context.
// UserPreferencesContext is the single source of truth; settings-page writes
// to it immediately when the theme dropdown changes.
const ThemeSync: React.FC = () => {
	const { currentUser, mode } = useApp();
	const { getUserPreferences } = useUserPreferences();
	const { setTheme } = useTheme();

	useEffect(() => {
		const savedTheme = getUserPreferences(currentUser.id).theme ?? "light";
		setTheme(savedTheme);
		if (mode === "demo") {
			try {
				localStorage.removeItem("cash-forecaster-theme");
			} catch (_) {
				/* ignore */
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentUser.id]);

	return null;
};

// Mapping of page names to required permissions
// Pages not listed here are accessible to all users (e.g. dashboard, settings)

const AppContent: React.FC = () => {
	const { currentUser, mode, setNavigate } = useApp();
	const { hasPermission, hasAnyPermission, isSystemAdmin, isLoading: permissionsLoading } = usePermissions();
	const {
		setCurrentPage: setAssistantPage,
		setCurrentUserId: setAssistantUserId,
	} = useAssistant();
	const { setCurrentRoleId } = useTour();
	const { getUserPreferences } = useUserPreferences();
	const { defaults: roleDefaults } = useUserPreferenceDefaults();

	// Three-layer landing page resolution:
	//   1. User's own My Preferences setting (highest priority — their explicit choice)
	//   2. Role-level default from UserPreferenceDefaults (admin-configurable per role)
	//   3. Smart permission fallback — admin card holders land on admin-dashboard;
	//      everyone else lands on dashboard. Never hardcoded to 'dashboard'.
	const resolveInitialPage = (): string => {
		// Layer 1: user's explicit personal preference
		const userPref = getUserPreferences(currentUser.id).defaultLandingPage;
		if (userPref) return userPref;

		// Layer 2: role-level default set by admin
		if (roleDefaults.defaultLandingPage) return roleDefaults.defaultLandingPage;

		// Layer 3: smart fallback — admin-only roles land on their dashboard
		const hasAnyDashboardCard = hasAnyPermission(
			"view_current_cash_mini_card",
			"view_bank_accounts_card",
			"view_executive_cash_position_card",
			"view_group_cash_position_card",
			"view_30day_forecast_mini_card",
			"view_90day_interactive_chart_card",
			"view_scenario_comparison_card",
			"view_recent_transactions_card",
			"view_upcoming_recurring_transactions_card",
			"view_overdue_invoices_mini_card",
			"view_upcoming_payments_mini_card",
			"view_actions_card",
			"view_variance_card",
			"view_uncategorized_items_card",
			"view_governance_compliance_card",
			"view_board_ready_summary_card",
			"view_critical_alerts_card",
		);
		if (!hasAnyDashboardCard) {
			const hasAnyAdminCard = hasAnyPermission(
				"view_data_sources_card",
				"view_last_sync_card",
				"view_recent_activity_feed",
			);
			if (hasAnyAdminCard) return "admin-dashboard";
		}
		return "dashboard";
	};

	const PAGE_TITLES: Record<string, string> = {
		transactions: "Transactions - Synced Transactions",
		"recurring-transactions": "Transactions - Recurring Transactions",
	};

	const [currentPage, setCurrentPage] = useState(() => resolveInitialPage());
	const [navigationData, setNavigationData] = useState<
		{ highlightSourceType?: string; accountFilter?: string } | undefined
	>(undefined);
	const [helpOpen, setHelpOpen] = useState(false);

	// Enhanced navigation handler that accepts optional data
	const handleNavigate = (
		page: string,
		data?: { highlightSourceType?: string; accountFilter?: string },
	) => {
		setCurrentPage(page);
		setNavigationData(data);
	};

	// Register handleNavigate globally so OnboardingAssistant (portal) can navigate
	useEffect(() => {
		setNavigate(handleNavigate);
	}, [setNavigate, handleNavigate]);

	// Sync current page with assistant context
	useEffect(() => {
		setAssistantPage(currentPage);
	}, [currentPage, setAssistantPage]);

	// Sync current user's role ID with tour context, and user ID with assistant context
	useEffect(() => {
		const roleId = getRoleTemplateId(currentUser.forecasterRole);
		setCurrentRoleId(roleId);
		setAssistantUserId(currentUser.id);
	}, [currentUser, setCurrentRoleId, setAssistantUserId]);

	const renderDashboard = () => {
		// Always show DynamicDashboard for the Dashboard menu item
		// DynamicDashboard will show welcome message if user has no dashboard cards
		return <DynamicDashboard onNavigate={handleNavigate} />;
	};

	const renderPage = () => {
		// Admin routes (no live mode check for admin)
		if (isSystemAdmin && mode === "demo") {
			switch (currentPage) {
				case "dashboard":
					return renderDashboard();
				case "admin-dashboard":
					return (
						<ProtectedRoute
							permission={[
								"view_data_sources_card",
								"view_last_sync_card",
								"view_recent_activity_feed",
							]}
						>
							<AdminDashboard onNavigate={handleNavigate} />
						</ProtectedRoute>
					);
				case "admin-general-settings":
					return (
						<ProtectedRoute permission="manage_general_settings">
							<GeneralSettings />
						</ProtectedRoute>
					);
				case "admin-terminology":
					return (
						<ProtectedRoute permission="manage_terminology">
							<SystemTerminologySettings />
						</ProtectedRoute>
					);
				case "admin-data-sources":
					return (
						<ProtectedRoute permission="configure_data_sources">
							<DataSourceConfiguration onNavigate={handleNavigate} />
						</ProtectedRoute>
					);
				case "admin-data-source-prioritisation":
					return (
						<DataSourcePrioritisation
							onNavigate={handleNavigate}
							highlightSourceType={navigationData?.highlightSourceType}
						/>
					);
				case "admin-category-mapping":
					return (
						<ProtectedRoute permission="configure_data_sources">
							<CategoryMappingPage />
						</ProtectedRoute>
					);
				case "bank-accounts":
					return (
						<ProtectedRoute permission="manage_bank_account_details">
							<BankAccountsPage />
						</ProtectedRoute>
					);

				case "admin-country-currency":
					return <CountryCurrencySetup />;
				case "admin-users":
					return <UserManagement />;
				case "admin-roles-permissions":
					return <RolesPermissionsContainer />;
				case "admin-approval-workflows":
					return <ApprovalWorkflowsPage />;
				case "admin-user-defaults":
					return <UserPreferenceDefaults />;
				case "admin-dashboard-layout":
					return (
						<ProtectedRoute permission="manage_dashboard_layout">
							<AdminDashboardLayout />
						</ProtectedRoute>
					);
				case "scenarios":
					return (
						<ProtectedRoute permission="view_scenarios">
							<ErrorBoundary>
								<NewScenariosPage />
							</ErrorBoundary>
						</ProtectedRoute>
					);
				case "reports":
					return (
						<ProtectedRoute
							permission={["view_reports", "manage_report_permissions"]}
						>
							<ReportsPage />
						</ProtectedRoute>
					);
				case "recurring-transactions":
					return (
						<ProtectedRoute permission="manage_recurring_transactions">
							<RecurringTransactionsPage />
						</ProtectedRoute>
					);
				case "actions":
					return <ActionsPage />;
				case "settings":
					return <SettingsPage />;
				default:
					return renderDashboard();
			}
		}

		switch (currentPage) {
			case "dashboard":
				return renderDashboard();
			case "forecast-90":
				return (
					<ProtectedRoute permission="view_forecasts">
						<ForecastTimeline />
					</ProtectedRoute>
				);
			case "forecast-12m":
				return (
					<ProtectedRoute permission="view_forecasts">
						<Forecast12Month />
					</ProtectedRoute>
				);
			case "forecast-3y":
				return (
					<ProtectedRoute permission="view_3year_forecast_screen">
						<Forecast3Year />
					</ProtectedRoute>
				);
			case "forecast-refresh":
				return (
					<ProtectedRoute permission="trigger_forecast_refresh">
						<ForecastRefreshPage />
					</ProtectedRoute>
				);
			case "transactions":
				return (
					<ProtectedRoute permission="view_transactions">
						<ErrorBoundary>
							<TransactionsPage accountFilter={navigationData?.accountFilter} />
						</ErrorBoundary>
					</ProtectedRoute>
				);
			case "scenarios":
				return (
					<ErrorBoundary>
						<NewScenariosPage />
					</ErrorBoundary>
				);
			case "variance":
				return (
					<ProtectedRoute permission="view_variance_analysis_screen">
						<ErrorBoundary>
							<VarianceAnalysisPage onNavigate={handleNavigate} />
						</ErrorBoundary>
					</ProtectedRoute>
				);
			case "recommendations":
				return (
					<ProtectedRoute
						permission={[
							"view_operational_insights",
							"view_strategic_insights",
						]}
					>
						<ErrorBoundary>
							<AIRecommendationsPage />
						</ErrorBoundary>
					</ProtectedRoute>
				);
			case "reports":
				return (
					<ProtectedRoute
						permission={["view_reports", "manage_report_permissions"]}
					>
						<ReportsPage />
					</ProtectedRoute>
				);
			case "recurring-transactions":
				return (
					<ProtectedRoute permission="manage_recurring_transactions">
						<ErrorBoundary>
							<RecurringTransactionsPage />
						</ErrorBoundary>
					</ProtectedRoute>
				);
			case "actions":
				return <ActionsPage />;
			// Admin routes - protected by specific permissions
			case "admin-dashboard":
				return (
					<ProtectedRoute
						permission={[
							"view_data_sources_card",
							"view_last_sync_card",
							"view_recent_activity_feed",
						]}
					>
						<AdminDashboard onNavigate={handleNavigate} />
					</ProtectedRoute>
				);
			case "admin-general-settings":
				return (
					<ProtectedRoute permission="manage_general_settings">
						<GeneralSettings />
					</ProtectedRoute>
				);
			case "admin-terminology":
				return (
					<ProtectedRoute permission="manage_terminology">
						<SystemTerminologySettings />
					</ProtectedRoute>
				);
			case "admin-data-sources":
				return (
					<ProtectedRoute permission="configure_data_sources">
						<DataSourceConfiguration onNavigate={handleNavigate} />
					</ProtectedRoute>
				);
			case "admin-category-mapping":
				return (
					<ProtectedRoute permission="configure_data_sources">
						<CategoryMappingPage />
					</ProtectedRoute>
				);
			case "bank-accounts":
				return (
					<ProtectedRoute permission="manage_bank_account_details">
						<BankAccountsPage />
					</ProtectedRoute>
				);

			case "admin-users":
				return (
					<ProtectedRoute permission="manage_users">
						<UserManagement />
					</ProtectedRoute>
				);
			case "admin-roles-permissions":
				return (
					<ProtectedRoute permission="manage_roles_permissions">
						<RolesPermissionsContainer />
					</ProtectedRoute>
				);
			case "admin-approval-workflows":
				return (
					<ProtectedRoute permission="manage_approval_workflows">
						<ApprovalWorkflowsPage />
					</ProtectedRoute>
				);
			case "admin-user-defaults":
				return (
					<ProtectedRoute permission="manage_user_defaults">
						<UserPreferenceDefaults />
					</ProtectedRoute>
				);
			case "admin-dashboard-layout":
				return (
					<ProtectedRoute permission="manage_dashboard_layout">
						<AdminDashboardLayout />
					</ProtectedRoute>
				);

			case "settings":
				return <SettingsPage />;
			default:
				return renderDashboard();
		}
	};

	if (permissionsLoading) return <LoadingScreen />;

	return (
		<div className="h-svh bg-background flex flex-col">
			<ThemeSync />
			<SidebarProvider defaultOpen={true} className="flex-1 min-h-0">
				<Sidebar
					currentPage={currentPage}
					onNavigate={setCurrentPage}
					data-tour="sidebar"
				/>
				<SidebarInset className="flex flex-col min-h-0 overflow-hidden">
					<Header
						pageTitle={PAGE_TITLES[currentPage]}
						onUserChange={(userId) =>
							setCurrentPage(
								getUserPreferences(userId).defaultLandingPage ?? "dashboard",
							)
						}
						onHelpOpen={() => setHelpOpen(true)}
					/>
					{/* Persistent forecast refresh status banner — appears across all screens when active */}
					<ForecastRefreshBanner />
					<div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 bg-muted/70">
						{mode === "demo" && currentPage === "dashboard" && (
							<div className="p-4">
								<Alert>
									<AlertDescription>
										You're viewing demo data for{" "}
										<strong>BuildRight Construction Ltd</strong>. Switch to Live
										Mode to connect your Access Financials account.
									</AlertDescription>
								</Alert>
							</div>
						)}
						{renderPage()}
					</div>
				</SidebarInset>
			</SidebarProvider>

			{/* Help Drawer */}
			<HelpDrawer
				open={helpOpen}
				onClose={() => setHelpOpen(false)}
				currentPage={currentPage}
				onNavigate={(pageKey) => {
					handleNavigate(pageKey);
					setHelpOpen(false);
				}}
			/>

			{/* Tours */}
			<TourMenu />
			<TourOverlay onNavigate={setCurrentPage} />
			{currentPage === "dashboard" && mode === "demo" && <WelcomePrompt />}

			{/* Onboarding Assistant — portal-rendered above all app chrome */}
			<OnboardingGate />
		</div>
	);
};

function App() {
	return (
		<AppProvider>
			<UsersProvider>
				<UserPreferencesProvider>
					<UserPreferenceDefaultsProvider>
						<PreferencesProvider>
							<JurisdictionProvider>
								<WootricWrapper>
									<AppStateProvider>
										<BaseForecastProvider>
											<ScenarioProvider>
												<ForecastRefreshProvider>
													<ActionsProvider>
														<RecommendationsProvider>
															<DataSourceProvider>
																<TourProvider>
																	<AssistantProvider>
																		<DrillDownProvider>
																			<BrowserRouter>
																				<OnboardingProvider>
																					<Routes>
																						<Route
																							path="/dashboard"
																							element={<AppContent />}
																						/>
																						<Route
																							path="/"
																							element={
																								<Navigate
																									to="/dashboard"
																									replace
																								/>
																							}
																						/>
																					</Routes>
																				</OnboardingProvider>
																			</BrowserRouter>
																			<Toaster />
																		</DrillDownProvider>
																	</AssistantProvider>
																</TourProvider>
															</DataSourceProvider>
														</RecommendationsProvider>
													</ActionsProvider>
												</ForecastRefreshProvider>
											</ScenarioProvider>
										</BaseForecastProvider>
									</AppStateProvider>
								</WootricWrapper>
							</JurisdictionProvider>
						</PreferencesProvider>
					</UserPreferenceDefaultsProvider>
				</UserPreferencesProvider>
			</UsersProvider>
		</AppProvider>
	);
}

export default App;
